import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CancelRounded from '@mui/icons-material/CancelRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import { GoogleIcon } from '@/components/GoogleIcon';
import { transition } from '@/theme';
import { initials } from '@/utils/initials';
import type { BrandState } from '@/marketplace/brands';
import { schemeFor } from '@/marketplace/format';
import { toLabel } from '../domain';
import { DOMAIN_TLDS, useDomainCheck, type DomainTld } from '../api';

type Choice = 'included' | 'own';

function useDebounced<T>(value: T, ms: number) {
  const [current, setCurrent] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setCurrent(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return current;
}

interface DomainLabProps {
  brand: BrandState;
  onWant: (domain: string | null) => void;
}

/**
 * The address the professional's students will type: a free subdomain of the
 * platform, or their own domain (a paid service we register and configure).
 * The availability check asks the official registry, live.
 */
export function DomainLab({ brand, onWant }: DomainLabProps) {
  const base = window.location.hostname;
  const [choice, setChoice] = useState<Choice>('own');
  // Follows the brand name typed above until the person edits the address.
  const [custom, setCustom] = useState<string | null>(null);
  const label = custom ?? (toLabel(brand.name) || 'suamarca');
  const setLabel = (value: string) => setCustom(value);
  const [tld, setTld] = useState<DomainTld>('app');

  const debounced = useDebounced(label, 500);
  const check = useDomainCheck(debounced, tld);
  const own = `${label || 'suamarca'}.${tld}`;
  const included = `${label || 'suamarca'}.${base}`;
  const shown = choice === 'own' ? own : included;
  const s = schemeFor(brand.color).light;
  const pending = choice === 'own' && (label !== debounced || check.isFetching);
  const availability =
    check.data?.domain === `${debounced}.${tld}` ? check.data.availability : null;

  const suggestions = [
    `${label}personal`,
    `${label}treino`,
    ...DOMAIN_TLDS.filter((t) => t !== tld).map((t) => `${label}.${t}`),
  ].slice(0, 4);

  const option = (key: Choice, title: string, address: string, tag: string, paid: boolean) => {
    const selected = choice === key;
    return (
      <ButtonBase
        onClick={() => setChoice(key)}
        aria-pressed={selected}
        sx={(theme) => ({
          width: '100%',
          display: 'block',
          textAlign: 'left',
          p: 2,
          borderRadius: 3,
          border: `2px solid ${selected ? theme.vars.palette.primary.main : theme.vars.palette.divider}`,
          bgcolor: selected
            ? theme.alpha(theme.vars.palette.primary.container, 0.45)
            : 'transparent',
          transition: transition(['border-color', 'background-color'], 'medium1'),
        })}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
        >
          <Typography variant="subtitle2">{title}</Typography>
          <Chip size="small" variant="soft" color={paid ? 'primary' : 'success'} label={tag} />
        </Stack>
        <Typography
          sx={{ fontFamily: 'monospace', fontSize: '1rem', mt: 0.75, wordBreak: 'break-all' }}
        >
          {address}
        </Typography>
      </ButtonBase>
    );
  };

  return (
    <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: 'center' }}>
      <Grid size={{ xs: 12, md: 5.5 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1}>
            <TextField
              label="Nome do endereço"
              value={label}
              onChange={(e) => {
                setLabel(
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, '')
                    .slice(0, 40),
                );
              }}
              sx={{ flex: 1 }}
              helperText="Sem acentos nem espaços. Ex.: anapersonal"
            />
            <TextField
              select
              label="Final"
              value={tld}
              onChange={(e) => setTld(e.target.value as DomainTld)}
              sx={{ width: 120 }}
              slotProps={{
                input: { startAdornment: <InputAdornment position="start">.</InputAdornment> },
              }}
            >
              {DOMAIN_TLDS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {option('own', 'Domínio próprio', own, 'Serviço à parte', true)}
          {option('included', 'Endereço incluso no plano', included, 'Incluso', false)}

          {choice === 'own' && (
            <Box aria-live="polite">
              {pending ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', color: 'text.secondary' }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="body2">Consultando o registro oficial…</Typography>
                </Stack>
              ) : availability === 'available' ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', color: 'success.main' }}
                >
                  <CheckCircleRounded fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {own} está livre agora!
                  </Typography>
                </Stack>
              ) : availability === 'taken' ? (
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', color: 'error.main' }}
                  >
                    <CancelRounded fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {own} já tem dono. Que tal:
                    </Typography>
                  </Stack>
                  <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                    {suggestions.map((item) => (
                      <Chip
                        key={item}
                        label={item.includes('.') ? item : `${item}.${tld}`}
                        variant="outlined"
                        onClick={() => {
                          if (item.includes('.')) {
                            const [name, ...rest] = item.split('.');
                            setLabel(name);
                            setTld(rest.join('.') as DomainTld);
                          } else {
                            setLabel(item);
                          }
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
              ) : availability === 'unknown' || check.isError ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', color: 'text.secondary' }}
                >
                  <HelpOutlineRounded fontSize="small" />
                  <Typography variant="body2">
                    Não deu para consultar agora. A gente confere para você.
                  </Typography>
                </Stack>
              ) : null}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Consulta feita agora no registro oficial do domínio. Ele só fica seu quando a gente
                registrar: cuidamos da compra, da configuração, do HTTPS e da renovação.
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={() => onWant(choice === 'own' ? own : null)}
            disabled={choice === 'own' && availability === 'taken'}
          >
            {choice === 'own' ? `Quero o ${own}` : 'Quero começar com o endereço incluso'}
          </Button>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 6.5 }}>
        {/* Browser window with the student's sign-in page, in the professional's brand. */}
        <Box
          role="img"
          aria-label={`Prévia do endereço ${shown}`}
          sx={(theme) => ({
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${theme.vars.palette.divider}`,
            boxShadow: `0 40px 90px -50px ${s.primary.dark}`,
            bgcolor: '#fff',
          })}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ px: 1.5, pt: 1.25, bgcolor: '#e8eaed', alignItems: 'flex-end' }}
          >
            <Stack direction="row" spacing={0.75} sx={{ pb: 1.25, pr: 1 }}>
              {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
                <Box key={c} sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: c }} />
              ))}
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                px: 1.5,
                py: 0.75,
                bgcolor: '#fff',
                borderRadius: '10px 10px 0 0',
                minWidth: 0,
                maxWidth: 240,
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '4px',
                  bgcolor: s.primary.main,
                  color: s.primary.contrastText,
                  fontSize: 8,
                  fontWeight: 800,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                {initials(brand.name || 'SM')}
              </Box>
              <Typography sx={{ fontSize: 12, color: '#202124' }} noWrap>
                {brand.name || 'Sua marca'}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              px: 1.5,
              py: 1,
              alignItems: 'center',
              bgcolor: '#fff',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flex: 1,
                alignItems: 'center',
                px: 1.5,
                py: 0.75,
                borderRadius: 99,
                bgcolor: '#f1f3f4',
                minWidth: 0,
              }}
            >
              <LockRounded sx={{ fontSize: 15, color: '#5f6368' }} />
              <Typography
                key={shown}
                sx={{
                  fontSize: 14,
                  color: '#202124',
                  fontFamily: 'inherit',
                  animation: 'addr 500ms ease',
                  '@keyframes addr': { from: { opacity: 0, transform: 'translateY(4px)' } },
                }}
                noWrap
              >
                <span style={{ color: '#5f6368' }}>https://</span>
                <b>{shown}</b>
              </Typography>
            </Stack>
          </Stack>
          <Box
            sx={{
              p: { xs: 3, sm: 5 },
              minHeight: 280,
              display: 'grid',
              placeItems: 'center',
              background: `radial-gradient(circle at 15% 10%, ${s.primary.container}, transparent 55%), radial-gradient(circle at 90% 90%, ${s.secondary.container}, transparent 50%), #fff`,
              transition: transition('background', 'long2'),
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 320,
                p: 3,
                borderRadius: `${Math.round(brand.radius * 1.4)}px`,
                bgcolor: '#fff',
                boxShadow: '0 20px 50px -30px rgba(0,0,0,0.45)',
                textAlign: 'center',
                color: '#1b1b1f',
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  mx: 'auto',
                  borderRadius: `${Math.round(brand.radius * 0.9)}px`,
                  bgcolor: s.primary.main,
                  color: s.primary.contrastText,
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 800,
                  fontSize: 18,
                  transition: transition(['background-color', 'border-radius'], 'long2'),
                }}
              >
                {initials(brand.name || 'SM')}
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18, mt: 1.5 }}>
                {brand.name || 'Sua marca'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#5f6368', mt: 0.5 }}>
                Entre para ver seus treinos de hoje
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mt: 2.5,
                  py: 1.1,
                  borderRadius: `${Math.round(brand.radius * 0.8)}px`,
                  border: '1px solid #dadce0',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                <GoogleIcon />
                <span>Entrar com Google</span>
              </Stack>
            </Box>
          </Box>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}
        >
          Assim fica a tela de entrada dos seus alunos, no seu endereço.
        </Typography>
      </Grid>
    </Grid>
  );
}
